import React, { use, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

import { Box, Typography } from "@mui/material";
import { addDays, differenceInDays, format, parse } from "date-fns";
import propTypes from "prop-types";

import InfoCard from "../../../components/Cards/InfoCard";
import PackageCard from "../../../components/Cards/PackageCard";
import PurchaseModal from "../../../components/Modals/PurchaseModal";
import PageHeader from "../../../components/PageHeader";
import PendingPaymentAlert from "../../../components/PendingPaymentAlert";
import InfoCardSkeleton from "../../../components/Skeletons/InfoCardSkeleton";
import PackageCardSkeleton from "../../../components/Skeletons/PackageCardSkeleton";
import { useFirstTimePlans, useMemberContracts, useRenewalPlans } from "../../../hooks";
import { useApiOptions } from "../../../hooks/DataFetching";
import { useRootHttpRequest } from "../../../services/api";
import { getFreePassPlansIds } from "../../../utils/helperFunctions";

const FREE_PASS_PLAN_IDS = getFreePassPlansIds();

const Packages = ({
  onPackageStartDateChange,
  onPaymentMethodChange,
  initiatePayment,
  onCloseModal,
  onPurchasePackage,
  payment,
}) => {
  const { t } = useTranslation();
  const { axiosInstance: rootApiClient } = useRootHttpRequest();
  const { apiClient, user, userHasPendingPayments, contractCharges } = useOutletContext();
  const [showRenewalPackages, setShowRenewalPackages] = useState(false);
  const [showFirstTimePurchase, setShowFirstTimePurchase] = useState(false);
  const [hasFreePass, setHasFreePass] = useState(false);

  const isExpiringWithinNextMonth = (dateString) => {
    const today = new Date();
    const expirationDate = new Date(dateString);
    const daysDifference = differenceInDays(expirationDate, today);

    return daysDifference <= 30;
  };

  const { data: clientSubscriptions, isLoading: loadingSubscriptions } = useMemberContracts({
    apiClient,
    memberContracts: user?.contracts,
    memberId: user?.id,
    userHasPendingPayments,
    contractCharges,
  });

  const packageStartDate = useMemo(() => {
    if (showRenewalPackages && clientSubscriptions?.length === 1) {
      const currentContractEndDate = clientSubscriptions[0].expires_at;
      const startDate = addDays(currentContractEndDate, 1);
      return format(startDate, "yyyy-MM-dd");
    } else if (hasFreePass) {
      const freePassSubscription = clientSubscriptions.find((sub) =>
        FREE_PASS_PLAN_IDS.includes(sub.paymentPlan.id),
      );
      if (freePassSubscription) {
        const freePassEndDate = freePassSubscription.expires_at;
        const startDate = addDays(freePassEndDate, 1);
        return format(startDate, "yyyy-MM-dd");
      }
    } else return format(new Date(), "yyyy-MM-dd");
  }, [showRenewalPackages, clientSubscriptions, hasFreePass]);

  const { data: apiOptions } = useApiOptions({ apiClient: rootApiClient });

  const minPackageStartDate = packageStartDate;
  const maxPackageStartDate = useMemo(() => {
    if (apiOptions?.max_start_date) {
      const parsedDate = parse(apiOptions.max_start_date, "dd/MM/yyyy", new Date());
      return format(parsedDate, "yyyy-MM-dd");
    } else {
      return minPackageStartDate;
    }
  }, [apiOptions]);

  const { data: renewalPackages, isPending: isPendingRenewalPackages } = useRenewalPlans({
    apiClient,
    showRenewalPackages,
    clubId: user?.homeClubId,
  });

  const { data: firstTimePackages, isPending: loadingFirstTimePackages } = useFirstTimePlans({
    apiClient,
    clubId: user?.homeClubId,
    showFirstTimePackages: showFirstTimePurchase,
  });

  const hasAutoRenewEnabled = (subscription) => {
    const paymentTypes = subscription?.paymentPlan?.allowedPaymentTypes;
    const customAttributes = subscription?.paymentPlan?.customAttributes;
    const isAutoRenewEnabled = customAttributes["Automatic Renewal"] === "True";
    return paymentTypes?.isRecurringCreditCardPaymentAllowed && isAutoRenewEnabled;
  };

  const getAutoRenewSubscriptions = useCallback(
    (subscriptions) => subscriptions?.filter(hasAutoRenewEnabled) ?? [],
    [],
  );

  useEffect(() => {
    const hasSubscriptions = clientSubscriptions.some(
      (sub) => !FREE_PASS_PLAN_IDS.includes(sub.paymentPlan.id),
    );

    const hasFreePass = clientSubscriptions.some((sub) =>
      FREE_PASS_PLAN_IDS.includes(sub.paymentPlan.id),
    );

    const shouldShowFirstTimePurchase = !hasSubscriptions;
    const autoRenewSubscriptions = getAutoRenewSubscriptions(clientSubscriptions);
    const hasAutoRenewal = autoRenewSubscriptions.length > 0;

    let showRenewal = false;

    if (clientSubscriptions.length === 1) {
      const expirationDate = clientSubscriptions[0]?.expires_at;
      showRenewal = expirationDate ? isExpiringWithinNextMonth(expirationDate) : true;
    }
    setHasFreePass(hasFreePass);
    setShowFirstTimePurchase(shouldShowFirstTimePurchase);
    setShowRenewalPackages(showRenewal && !hasFreePass && !hasAutoRenewal);
  }, [clientSubscriptions, getAutoRenewSubscriptions]);

  useEffect(() => {
    if (onPackageStartDateChange) onPackageStartDateChange(packageStartDate);
  }, [packageStartDate, onPackageStartDateChange]);

  let firstTimePackageElements = null;
  if (showFirstTimePurchase) {
    firstTimePackageElements = (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {firstTimePackages?.map((item) => {
          return (
            <PackageCard
              key={item.id}
              onPurchasePackage={onPurchasePackage}
              packageData={item}
              purchaseDisabled={userHasPendingPayments}
            />
          );
        })}
      </Box>
    );
  }

  return (
    <Box>
      {userHasPendingPayments && <PendingPaymentAlert />}
      {payment.selectedPackage && (
        <PurchaseModal
          isOpen={payment.modalOpen}
          isPending={payment.loading}
          onClose={onCloseModal}
          onProceed={initiatePayment}
          paymentMethod={payment.method}
          onPaymentMethodChange={onPaymentMethodChange}
          onPackageStartDateChange={onPackageStartDateChange}
          data={{
            name: payment.selectedPackage.name,
            description: payment.selectedPackage.description,
            price: payment.selectedPackage.price,
            paymentMethods: payment.selectedPackage.paymentMethods,
            startDate: packageStartDate,
            maxStartDate: maxPackageStartDate,
            minStartDate: minPackageStartDate,
            enableCustomStartDate: apiOptions?.enable_custom_start_date,
          }}
        />
      )}
      <PageHeader title={t("packages")} />
      {loadingSubscriptions && <InfoCardSkeleton />}
      {clientSubscriptions.length > 0 && (
        <>
          <Typography
            variant="h5"
            component="h2"
            fontWeight="fontWeightMedium"
            sx={{ marginTop: 2, marginBottom: 1.5 }}
          >
            {t("packagesModule.currentPackages")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {clientSubscriptions.map((item) => (
              <InfoCard
                key={item.id}
                bgColor="customColors.bg2"
                title={item.name}
                subTitle={{
                  text: t("packagesModule.myPackages"),
                  target: "/settings/subscriptions",
                }}
                data={[
                  {
                    title: t("packagesModule.packagePrice"),
                    value: { text: item.formattedPrice, isLink: false, target: null },
                  },
                  {
                    title: "",
                    value: {
                      text: "",
                      isLink: false,
                      target: null,
                    },
                  },
                  {
                    title: t("purchaseDate"),
                    value: {
                      text: item.startDate,
                      isLink: false,
                      target: null,
                    },
                  },
                  {
                    title: t("validTill"),
                    value: {
                      text: item.expires_at,
                      isLink: false,
                      target: null,
                    },
                  },
                ]}
              />
            ))}
          </Box>
        </>
      )}
      {showRenewalPackages && isPendingRenewalPackages && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, marginTop: 3 }}>
          <PackageCardSkeleton />
          <PackageCardSkeleton />
        </Box>
      )}
      {showRenewalPackages && !isPendingRenewalPackages && (
        <>
          <Typography
            variant="h5"
            component="h2"
            fontWeight="fontWeightMedium"
            sx={{ marginTop: 4, marginBottom: 1.5 }}
          >
            {t("packagesModule.renewalPackages")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {renewalPackages.map((item) => {
              return (
                <PackageCard
                  key={item.id}
                  onPurchasePackage={onPurchasePackage}
                  packageData={item}
                  purchaseDisabled={userHasPendingPayments}
                />
              );
            })}
          </Box>
        </>
      )}

      {showFirstTimePurchase && !loadingFirstTimePackages && (
        <Typography
          variant="h5"
          component="h2"
          fontWeight="fontWeightMedium"
          sx={{ marginTop: 4, marginBottom: 1.5 }}
        >
          {t("packagesModule.packages")}
        </Typography>
      )}

      {showFirstTimePurchase && loadingFirstTimePackages && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, marginTop: 2 }}>
          <PackageCardSkeleton />
          <PackageCardSkeleton />
        </Box>
      )}
      {firstTimePackageElements}
    </Box>
  );
};

Packages.propTypes = {
  onPackageStartDateChange: propTypes.func.isRequired,
  onPaymentMethodChange: propTypes.func.isRequired,
  initiatePayment: propTypes.func.isRequired,
  onCloseModal: propTypes.func.isRequired,
  onPurchasePackage: propTypes.func.isRequired,
  payment: propTypes.object.isRequired,
};

export default Packages;
